import { Button, Card, Flex, Text } from '@servicetitan/anvil2';
import { DataItem, formatCurrency } from '@servicetitan/financing-utils-shared';
import { ApplicationDataStore } from '@servicetitan/financing-application-shared';
import { SelectedPlanStore } from '@servicetitan/financing-plans-shared';
import { PaymentStructure } from '@servicetitan/financing-api-client';

interface PlanConfirmationProps {
    onConfirm?: () => void;
    disabled?: boolean;
    /** Customer-facing second-look provider name, resolved by the parent workflow. */
    providerName?: string;
}

export const PlanConfirmation = ({
    onConfirm,
    disabled = false,
    providerName = '',
}: PlanConfirmationProps) => {
    const { applicationData } = ApplicationDataStore.useStore();
    const { selectedPlan } = SelectedPlanStore.useStore();

    const formatPaymentDescription = () => {
        if (!selectedPlan) {
            return '';
        }

        // `Calculation` (fin-plans-api) and the imported `PaymentStructure` (financing-api) are two
        // identically-valued generated enums; reconcile them so the comparisons are enum-type-safe.
        const paymentStructure = selectedPlan.paymentStructure as PaymentStructure;
        const monthlyPayment =
            paymentStructure === PaymentStructure.DeferredInterestAndPrincipal
                ? selectedPlan.promotionalMonthlyPayment
                : selectedPlan.defaultMonthlyPayment;
        const terms =
            paymentStructure === PaymentStructure.DeferredInterestAndPrincipal
                ? selectedPlan.promotionalTerms
                : selectedPlan.defaultTerms;
        return `${formatCurrency(monthlyPayment)}/mo. for ${terms} months`;
    };

    const formatDeferredInterest = () => {
        if (!selectedPlan?.promotionalTerms) {
            return 'N/A';
        }
        return `${selectedPlan.promotionalTerms} months`;
    };

    return (
        <Flex direction="column" alignItems="center" style={{ width: '100%' }}>
            <Flex
                direction="column"
                gap="6"
                style={{ maxWidth: '845px', width: 'calc(100% - 48px)', marginTop: '50px' }}
            >
                {/* Title */}
                <Text variant="headline" size="medium" el="h1">
                    Confirm your {providerName} offer
                </Text>

                {/* Description */}
                <Text variant="body" color="secondary">
                    After you review and confirm your selected offer, you will receive an email and
                    text message with a link to complete your application. Once your selection is
                    confirmed, you cannot change it without submitting a new prequalification
                    application.
                </Text>

                {/* Offer Details Card */}
                <Card flexDirection="column" gap="4" style={{ width: '100%' }}>
                    <Text variant="headline" size="medium" el="h3">
                        Offer Details
                    </Text>

                    <Flex direction="column" gap="4">
                        <Flex direction="row" gap="4" style={{ flexWrap: 'wrap' }}>
                            <DataItem
                                label="Loan Amount"
                                text={formatCurrency(selectedPlan?.loanAmount ?? 0)}
                                style={{ flex: '1 1 200px' }}
                            />
                            <DataItem
                                label="Credit Limit"
                                text={formatCurrency(selectedPlan?.maxLoanAmount ?? 0)}
                                style={{ flex: '1 1 200px' }}
                            />
                        </Flex>
                        <Flex direction="row" gap="4" style={{ flexWrap: 'wrap' }}>
                            <DataItem
                                label="Min. Payment"
                                text={formatPaymentDescription()}
                                style={{ flex: '1 1 200px' }}
                            />
                            <DataItem
                                label="Deferred Interest"
                                text={formatDeferredInterest()}
                                style={{ flex: '1 1 200px' }}
                            />
                        </Flex>
                    </Flex>
                </Card>

                {/* Contact Information Card */}
                <Card flexDirection="column" gap="4" style={{ width: '100%' }}>
                    <Flex direction="column" gap="1">
                        <Text variant="headline" size="medium" el="h3">
                            Contact Information
                        </Text>
                        <Text variant="body" color="secondary">
                            To update the applicant's contact information, you must submit another
                            prequalification application. This will not impact the applicant's
                            credit.
                        </Text>
                    </Flex>

                    <Flex direction="row" gap="4" style={{ flexWrap: 'wrap' }}>
                        <DataItem
                            label="Email Address"
                            text={
                                applicationData.applicant.primaryApplicant?.email ?? 'Not provided'
                            }
                            style={{ flex: '1 1 200px' }}
                        />
                        <DataItem
                            label="Phone Number"
                            text={
                                applicationData.applicant.primaryApplicant?.primaryPhone ??
                                'Not provided'
                            }
                            style={{ flex: '1 1 200px' }}
                        />
                    </Flex>
                </Card>

                {/* Confirm Button */}
                <Button
                    appearance="primary"
                    size="large"
                    onClick={onConfirm}
                    disabled={disabled}
                    loading={disabled}
                    style={{ width: '100%' }}
                >
                    Confirm Selection
                </Button>
            </Flex>
        </Flex>
    );
};
